import { useState } from 'react';
import { api } from '../lib/api';
import PanelBrandLockup from '../components/ui/PanelBrandLockup';
import { PublicPanelCard, PublicPanelShell } from '../components/ui/PublicPanelShell';

interface Props {
  onComplete: () => void;
}

function toSlug(name: string) {
  return name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

export default function SetupPage({ onComplete }: Props) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [tenantName, setTenantName] = useState('');
  const [tenantSlug, setTenantSlug] = useState('');
  const [slugEdited, setSlugEdited] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function handleNameChange(value: string) {
    setTenantName(value);
    if (!slugEdited) setTenantSlug(toSlug(value));
  }

  function handleSlugChange(value: string) {
    setTenantSlug(value);
    setSlugEdited(true);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email || !password || !tenantName || !tenantSlug) return;
    setLoading(true);
    setError(null);
    try {
      await api.setup.create(email, password, tenantName, tenantSlug);
      onComplete();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <PublicPanelShell>
      <div className="mb-8 w-full">
        <PanelBrandLockup
          title="Configuración inicial"
          subtitle="Crea la primera cuenta administradora y el primer sitio de este ambiente."
          compact
        />
      </div>
      <PublicPanelCard>
        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="space-y-1">
            <h2 className="text-xl font-semibold text-ui-text">Arranque del panel</h2>
            <p className="text-sm text-ui-text-muted">De aquí saldrá la cuenta principal y el primer sitio.</p>
          </div>

          <div className="space-y-3">
            <div>
              <label className="block text-sm text-ui-text-muted mb-1">Email del administrador</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
                placeholder="tu@email.com"
                className="w-full bg-ui-bg border border-ui-border rounded-lg px-3 py-2 text-sm text-ui-text placeholder-ui-text-subtle focus:outline-none focus:border-ui-accent"
              />
            </div>
            <div>
              <label className="block text-sm text-ui-text-muted mb-1">Contraseña</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="••••••••"
                className="w-full bg-ui-bg border border-ui-border rounded-lg px-3 py-2 text-sm text-ui-text placeholder-ui-text-subtle focus:outline-none focus:border-ui-accent"
              />
            </div>
          </div>

          <div className="border-t border-ui-border pt-4 space-y-3">
            <p className="text-sm text-ui-text-subtle">Primer sitio</p>
            <div>
              <label className="block text-sm text-ui-text-muted mb-1">Nombre del sitio</label>
              <input
                type="text"
                value={tenantName}
                onChange={(e) => handleNameChange(e.target.value)}
                required
                placeholder="Mi Empresa"
                className="w-full bg-ui-bg border border-ui-border rounded-lg px-3 py-2 text-sm text-ui-text placeholder-ui-text-subtle focus:outline-none focus:border-ui-accent"
              />
            </div>
            <div>
              <label className="block text-sm text-ui-text-muted mb-1">Slug</label>
              <input
                type="text"
                value={tenantSlug}
                onChange={(e) => handleSlugChange(e.target.value)}
                required
                placeholder="mi-empresa"
                className="w-full bg-ui-bg border border-ui-border rounded-lg px-3 py-2 text-sm text-ui-text font-mono placeholder-ui-text-subtle focus:outline-none focus:border-ui-accent"
              />
            </div>
          </div>

          {error && <p className="text-sm text-ui-danger">{error}</p>}

          <button
            type="submit"
            disabled={loading}
            className="w-full px-4 py-2.5 bg-ui-accent text-ui-text-inverse text-sm font-medium rounded-lg disabled:opacity-50 hover:opacity-90 transition-opacity"
          >
            {loading ? 'Inicializando…' : 'Inicializar panel'}
          </button>
        </form>
      </PublicPanelCard>
    </PublicPanelShell>
  );
}
